import { useCallback, useEffect, useMemo, useRef } from 'react';
import { getQueryStringResults } from '@plone/volto/actions/querystringsearch/querystringsearch';
import hoistNonReactStatics from 'hoist-non-react-statics';
import { useDispatch, useSelector } from 'react-redux';

import { shallowEqual, useQueryItems } from './utils';

function getDisplayName(WrappedComponent) {
  return WrappedComponent.displayName || WrappedComponent.name || 'Component';
}

export default function withUIDQueryResults(
  WrappedComponent,
  dataPropertyName,
  metadata_fields,
) {
  function WithUIDQueryResults(props) {
    const dispatch = useDispatch();
    const { data = {} } = props;

    const id = useMemo(() => props.id || data.block, [props.id, data.block]);

    // Use ref to track previous value
    const previousUids = useRef([]);
    const propertyValue = data[dataPropertyName];

    const { uids } = useQueryItems(propertyValue);

    const query = useMemo(
      () => [
        {
          i: 'UID',
          o: 'plone.app.querystring.operation.string.is',
          v: uids,
        },
      ],
      [uids],
    );

    const querystringResult = useSelector((state) => {
      return state?.querystringsearch?.subrequests?.[id] || null;
    }, shallowEqual);

    const hasLoaded = uids.length ? querystringResult?.loaded : true;

    const resultItems = useMemo(
      () =>
        querystringResult?.items && Array.isArray(querystringResult.items)
          ? querystringResult.items
          : [],
      [querystringResult?.items],
    );

    const resultObject = useMemo(() => {
      return resultItems.reduce((accumulator, item) => {
        if (item && item['UID']) {
          accumulator[item['UID']] = item;
        }
        return accumulator;
      }, {});
    }, [resultItems]);

    const listingItems = useMemo(() => {
      return resultItems.length
        ? uids
            .map((uid) => resultObject[uid])
            .filter((item) => item !== undefined)
        : [];
    }, [uids, resultObject, resultItems.length]);

    const fetchQueryResults = useCallback(() => {
      if (uids.length) {
        dispatch(
          getQueryStringResults(
            '/',
            {
              b_size: 1000,
              query: query,
              metadata_fields: metadata_fields,
            },
            id,
          ),
        );
      }
    }, [dispatch, uids, query, id]);

    // Effect otimizado para evitar chamadas desnecessárias
    useEffect(() => {
      const hasUids = uids.length > 0;
      const noResults = !querystringResult;
      const needsLoading =
        !querystringResult?.loaded && !querystringResult?.loading;

      // Verificar se há UIDs novos que não estavam na consulta anterior
      const previousUidsSet = new Set(previousUids.current);
      const hasNewUids = uids.some((uid) => !previousUidsSet.has(uid));

      // Só atualiza se houver UIDs novos ou não houver resultados/carregamento
      if (hasUids && (noResults || needsLoading || hasNewUids)) {
        previousUids.current = uids;
        fetchQueryResults();
      }
    }, [uids, querystringResult, fetchQueryResults]);

    return (
      <WrappedComponent
        {...props}
        listingItems={listingItems}
        hasLoaded={hasLoaded}
      />
    );
  }

  WithUIDQueryResults.displayName = `WithUIDQueryResults(${getDisplayName(WrappedComponent)})`;

  return hoistNonReactStatics(WithUIDQueryResults, WrappedComponent);
}
